import { PrimeProvider } from 'iam/ProviderStore'
import { PrimeUser } from 'iam/UserStore'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { collection } from 'infra/collection'
import { CursorPaged } from 'types/definitions'
import { WorkspacePostingAPI } from './staffing/API'
import {
  CreateWorkspacePostingPayload,
  UpdateWorkspacePostingPayload,
  WorkspacePostingAggregationQuery,
  WorkspacePostingsQuery,
} from './staffing/dtos/payloads'
import {
  WorkspacePostingAggregationDTO,
  WorkspacePostingDTO,
} from './staffing/dtos/workspace-posting'
import WorkspacePosting from './staffing/WorkspacePosting'
import { WorkspacePostingAggregation } from './staffing/WorkspacePostingAggregation'

export default class WorkspacePostingStore {
  /**
   * Workspace posting collection.
   */
  workspacePostings = collection<WorkspacePostingDTO, WorkspacePosting>({
    idAttribute: 'id',
    create: () =>
      new WorkspacePosting(
        this.primeWorkspace,
        this.primeUser,
        this.primeProvider
      ),
  })

  /**
   * Workspace posting status aggregation collection.
   */
  workspacePostingAggregations = collection<
    WorkspacePostingAggregationDTO,
    WorkspacePostingAggregation
  >({
    create: () => new WorkspacePostingAggregation(),
    getDataId: (json) => json.status,
    getModelId: (model) => model.status,
  })

  constructor(
    private workspacePostingAPI: WorkspacePostingAPI,
    private primeWorkspace: PrimeWorkspace,
    private primeUser: PrimeUser,
    private primeProvider: PrimeProvider
  ) {}

  /**
   * Fetch workspace postings from API.
   * @param query
   * @returns
   */
  async fetchWorkspacePostings(
    query: WorkspacePostingsQuery
  ): Promise<CursorPaged<WorkspacePosting>> {
    return this.workspacePostingAPI
      .list(query)
      .then((r) => CursorPaged.map(r, () => this.workspacePostings.set(r.data)))
  }

  /**
   * Fetch a single workspace posting
   * @param workspacePostingId
   */
  async fetchWorkspacePosting(workspacePostingId: string): Promise<void> {
    const updated = await this.workspacePostingAPI.get(workspacePostingId)
    this.workspacePostings.set(updated)
  }

  /**
   * Fetch workspace posting aggregations from API.
   * @param query
   */
  async fetchAggregations(
    query: WorkspacePostingAggregationQuery
  ): Promise<void> {
    this.workspacePostingAggregations.reset(
      await this.workspacePostingAPI.getAggregation(query)
    )
  }

  /**
   * Create a workspace posting.
   * @param workspaceId
   * @param payload
   */
  async createWorkspacePosting(
    workspaceId: string,
    payload: CreateWorkspacePostingPayload
  ): Promise<void> {
    const posting = await this.workspacePostingAPI.create(workspaceId, payload)
    this.workspacePostings.set(posting)
  }

  /**
   * Update a workspace posting.
   * @param id
   * @param payload
   */
  async updateWorkspacePosting(
    id: string,
    payload: UpdateWorkspacePostingPayload
  ): Promise<void> {
    const posting = await this.workspacePostingAPI.updateDraft(id, payload)
    this.workspacePostings.set(posting)
  }

  /**
   * Publish a workspace posting.
   * @param id
   */
  async publishWorkspacePosting(id: string): Promise<void> {
    await this.workspacePostingAPI.publish(id)
  }

  /**
   * Mark a workspace posting as in review.
   * @param id
   */
  async markInReview(id: string): Promise<void> {
    await this.workspacePostingAPI.markInReview(id)
  }

  /**
   * Invite an applicant to participate in a workspace posting.
   * @param id
   * @param applicantPublicId
   */
  async inviteApplicant(id: string, applicantPublicId: string): Promise<void> {
    await this.workspacePostingAPI.inviteApplicant(id, applicantPublicId)
  }

  /**
   * Rescind an invitation to an applicant.
   * @param id
   * @param applicantPublicId
   */
  async rescindInvitation(id: string, applicantPublicId: string) {
    await this.workspacePostingAPI.rescindInvitation(id, applicantPublicId)
  }
}
